import classNames from 'classnames';
import { CSSProperties, useEffect, useState } from 'react';
import { Button } from 'palette/Button';
import { Icon } from 'palette/Icon';
import { Transition } from 'palette/Transition';
import { BodyText } from 'palette/Typography';
import useToast from './hooks/useToast';
import { ToastWrapperProps } from './types';
import './styles/Toast.scss';

const timeout = 'slow';

function Toast({
    position = 'bottom-center',
    autoHide = false,
    isFloating = false,
    toastId,
    className,
    top,
    right,
}: ToastWrapperProps) {
    const { toastList, removeToast } = useToast();
    const [inlineStyle, setInlineStyle] = useState<CSSProperties>({});

    const deleteToast = (id: string | undefined) => {
        const currentToast = toastList?.find(toast => toast.id === id);

        if (id) removeToast(id);
        if (currentToast?.onClose) currentToast.onClose();
    };

    const onClickToast = (id: string | undefined) => {
        const currentToast = toastList?.find(toast => toast.id === id);

        if (id) removeToast(id);
        if (currentToast?.onClick) currentToast.onClick();
    };

    useEffect(() => {
        if (isFloating) {
            const tempStyle: CSSProperties = {};

            if (right) {
                tempStyle.right = +right;
                tempStyle.left = 'auto';
                tempStyle.transform = 'none';
            }

            if (top) {
                tempStyle.top = +top;
                tempStyle.bottom = 'auto';
                tempStyle.transform = 'none';
            }

            setInlineStyle(tempStyle);
        }

        const autoHideDuration = toastList?.length && toastList[0] && toastList[0].autoHideDuration;

        const timer = setTimeout(() => {
            if (autoHide && toastList?.length && toastList[0].id) { deleteToast(toastList[0].id); }
        }, autoHideDuration);

        return () => clearTimeout(timer);
        // eslint-disable-next-line
    }, [toastList, autoHide]);

    return (
        <div
            className={classNames(className,
                'palette-toasts',
                {
                    'palette-toasts--floating': isFloating,
                    [`palette-toasts--${position}`]: position && isFloating,
                    'palette-toasts--auto-fit': (right || top) && isFloating,
                })}
            style={inlineStyle}
        >
            {toastList?.filter(toast => toast.wrapperId === toastId).map(({
                id,
                children,
                type = 'default',
                iconPosition = 'left',
                iconProps,
                title,
                message,
                buttonProps,
            }) => (
                <Transition
                    key={id}
                    appear
                    show
                    property="opacity"
                    timeout={timeout}
                >
                    {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events */}
                    <div
                        className={classNames(
                            className,
                            'palette-toasts__item',
                            `palette-toasts__item--${type}`,
                        )}
                        onClick={() => onClickToast(id)}
                    >
                        {!children ? (
                            <>
                                <div className="palette-toasts__item__left-column">
                                    {iconProps && iconPosition === 'left' && (
                                        <Icon
                                            {...iconProps}
                                            className="palette-toasts__item__icon"
                                            color="white"
                                        />
                                    )}
                                    <div className="palette-toasts__item__description">
                                        {title && (
                                            <BodyText
                                                className="palette-toasts__item__title"
                                                color="white"
                                                level="1"
                                            >
                                                {title}
                                            </BodyText>
                                        )}
                                        {message && (
                                            <BodyText
                                                className="palette-toasts__item__txt"
                                                color="white"
                                                level="2"
                                            >
                                                {message}
                                            </BodyText>
                                        )}
                                    </div>
                                </div>
                                {buttonProps && (
                                    <Button
                                        className={classNames(`palette-toasts__item__btn--${type}`,
                                            'palette-toasts__item__btn')}
                                        {...buttonProps}
                                        iconColor="white"
                                    />
                                )}
                                {iconProps && iconPosition === 'right' && (
                                    <Icon
                                        {...iconProps}
                                        color="white"
                                    />
                                )}
                            </>
                        ) : (
                            children
                        )}
                    </div>
                </Transition>
            ))}
        </div>
    );
}

export default Toast;
